import { useLocation, Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import endpoints from "../../services/endpoints";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import usePrivilege from "../../components/CustomHooks/usePrivilege";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { setPreviousUrl, setUserUrl } from "../../store/slices/geolocationSlice";
import { resetPhoneNumbers } from "../../store/slices/phoneNumbersSlice";
import { setSelectedUser, setSelectedUserData, clearSelectedUser } from "../../store/slices/selectedUserSlice";
import { resetState } from "../../store/slices/listSlice";
import { setSelectedBankId } from "../../store/slices/selectedBankIdSlice";
import GridTable from "../../components/GridTable/GridTable";
import Image from "../../components/images/Image";
import { getTableDetailsByType } from "./components/EtopupRechargeTableHeaders";
import { getFilterConfigByType, useFetchMnoNames } from "./components/EtopupRechargeFilterConfig";
import useApi from "../../services/useApi";
import { renderExtraHeadingsByType } from "./renderExtraHeadings";
import { renderTopBarExtrasByType } from "./renderTopBarExtras";

type ReportTab = {
    name: string;
    route: string;
    privilege: string;
    searchPlaceholders: string; // comma separated
};

export default function EtopupRecharge({ type, params }: { type: string, params: any }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
    const currentUrl = location.pathname + location.search;
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch<AppDispatch>();
    const { etopupRechargeReports, unsuccessfulTransactionReports, etopupPurchaseOrderReports, reportsUsers, stockAccountStatement, mnoWiseEtopupPurchaseReports, moneyAccountStatement, mnoWiseEtopupAvailableBalanceReports, upfrontSales, mnoServiceFeeEarnedReports, customerMerchantBalance, geographicalSalesReports, onTargetAchievementOfMNO, upfrontPurchaseReports, reversalReports, etopupStockReversal, profitabilityReport, cvmReport, etopUpSalesTrendReports } = endpoints;
    const [searchPlaceholder, setSearchPlaceholder] =
        useState<string>("transaction id");
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
    const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
    const [canScrollRight, setCanScrollRight] = useState<boolean>(true);
    const scrollRef = useRef<HTMLDivElement>(null);
    const viewPrivilege = usePrivilege('RR2');
    const [fetchListTrigger, setFetchListTrigger] = useState<boolean>(false);
    const [userOptions, setUserOptions] = useState<string[]>([]);
    // Get selected user from Redux store
    const { selectedUser, selectedUserData } = useSelector((state: RootState) => state.selectedUser);
    const [usersData, setUsersData] = useState<Array<{
        first_name?: string;
        last_name?: string;
        user_code?: string;
        account_id?: string;
        stock_account_id?: string;
        money_account_id?: string;
    }>>([]);
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
    const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
    const [hasMoreUsers, setHasMoreUsers] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<'stock' | 'money'>('stock');
    const [walletBalanceActiveTab, setWalletBalanceActiveTab] = useState<'etopup-stocks' | 'digital-wallet'>('etopup-stocks');
    const [reversalActiveTab, setReversalActiveTab] = useState<'recharge-reversal' | 'etopup-stock-digital-wallet-reversal'>('recharge-reversal');
    const [selectedMnos, setSelectedMnos] = useState<string[]>(['All']);
    const [mnoOptions, setMnoOptions] = useState<Array<{ id: string, name: string }> | undefined>(undefined);
    const [mnoApiFailed, setMnoApiFailed] = useState<boolean>(false);
    const mnoFetchedRef = useRef(false);
    const { getAPI } = useApi();
    const { fetchMnoNames } = useFetchMnoNames();

    // Get privileges once; use pure checks to avoid calling hooks in loops
    const privilegesData = useSelector((state: RootState) => state?.privileges?.privileges as unknown);
    const isSuperAdmin = typeof privilegesData === 'string' && privilegesData === 'superadmin';
    const hasPrivilegePure = (privilege: string) => {
        if (isSuperAdmin) return true;
        const arr = Array.isArray(privilegesData) ? privilegesData : [];
        return arr.includes(privilege);
    };

    const checkScrollPosition = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
        }
    };

    const scrollLeft = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const fetchUsers = async (searchValue: string, pageNumber: number = 1, isLoadMore: boolean = false) => {
        try {
            const searchParam = searchValue ? `search=${encodeURIComponent(searchValue)}` : '';
            const pageParam = `page_number=${pageNumber}`;
            const params = searchParam ? `?${searchParam}&${pageParam}` : `?${pageParam}`;

            const response = await getAPI(`${reportsUsers}${params}`);

            if (!response.error && response.data && response.data.data) {
                // Store raw user data for later use
                const rawUsers = response.data.data;

                // Check if there's more data available
                const hasMore = rawUsers.length > 0;
                setHasMoreUsers(hasMore);

                // Format user names as "FirstName LastName (user_code)" or just "FirstName (user_code)" if no last name
                const users = rawUsers.map((user: {
                    first_name?: string;
                    last_name?: string;
                    user_code?: string;
                }) => {
                    const firstName = user.first_name || '';
                    const lastName = user.last_name || '';
                    const userCode = user.user_code || '';
                    const fullName = lastName ? `${firstName} ${lastName}` : firstName;
                    return userCode ? `${fullName} (${userCode})` : fullName;
                });

                if (isLoadMore) {
                    // Append new users to existing options for pagination
                    setUserOptions(prevOptions => [...prevOptions, ...users]);
                    setUsersData(prevData => [...prevData, ...rawUsers]);
                } else {
                    // Replace options for new search or initial load
                    setUserOptions(users);
                    setUsersData(rawUsers);
                    setHasMoreUsers(hasMore); // Reset hasMoreUsers for new search
                }
            } else {
                // No data returned
                setHasMoreUsers(false);
                if (!isLoadMore) {
                    setUserOptions([]);
                    setUsersData([]);
                }
            }

        } catch (error) {
            console.error('Error fetching users:', error);
            setHasMoreUsers(false);
            if (!isLoadMore) {
                setUserOptions([]);
                setUsersData([]);
            }
        }
    };

    useEffect(() => {
        if (type === 'Account Statement Report') {
            setDropdownLoading(true)
            setCurrentPageNumber(1);
            setCurrentSearchTerm('');
            fetchUsers('', 1, false);
            setDropdownLoading(false)

            // Check if there are filters applied but no user selected (e.g., after page refresh)
            // Only clear filters if we're sure there's no user selection in progress
            const hasFilters = searchParams.get('transaction_start_date') || searchParams.get('transaction_end_date') ||
                Array.from(searchParams.keys()).some(key => key !== 'page_number' && key !== 'sort');

            if (hasFilters && !selectedUser && !selectedUserData) {
                // Clear all filters by navigating to the same route without search params
                navigate(location.pathname, { replace: true });
            }
        }
    }, [type, selectedUser, selectedUserData, navigate, location.pathname]);

    const handleUserSelection = (_id: string, value: string) => {
        dispatch(setSelectedUser(value));
        // Find the corresponding user data from the raw users data
        const selectedUserIndex = userOptions.findIndex(option => option === value);
        if (selectedUserIndex !== -1 && usersData[selectedUserIndex]) {
            const userData = usersData[selectedUserIndex];
            const accountId = activeTab === 'money' ? userData.money_account_id : userData.stock_account_id;
            dispatch(setSelectedUserData({
                firstName: userData.first_name || '',
                lastName: userData.last_name || '',
                userCode: userData.user_code || '',
                account_id: accountId || '',
                stock_account_id: userData.stock_account_id || '',
                money_account_id: userData.money_account_id || ''
            }));
        } else {
            dispatch(setSelectedUserData(null));
        }
    };

    const handleUserSearch = (value: string) => {
        setCurrentSearchTerm(value);
        setCurrentPageNumber(1); // Reset page number when searching
        setHasMoreUsers(true); // Reset hasMoreUsers when search changes
        fetchUsers(value, 1, false); // Always fetch users, whether searching or showing all
    };

    const handleTransactionSearch = (searchTerm: string) => {
        // Update URL search params to trigger GridTable refetch with search term
        const currentParams = new URLSearchParams(location.search);
        if (searchTerm.trim()) {
            currentParams.set('search', searchTerm.trim());
        } else {
            currentParams.delete('search');
        }
        currentParams.set('page_number', '1'); // Reset to first page
        navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
    };

    const loadMoreUsers = async (_searchValue: string, isPageNumberChange?: boolean) => {
        if (isPageNumberChange && hasMoreUsers) {
            const nextPage = currentPageNumber + 1;
            setCurrentPageNumber(nextPage);
            await fetchUsers(currentSearchTerm, nextPage, true); // Load more with current search term
        }
    };

    const handleTabSwitch = (tab: 'stock' | 'money') => {
        setActiveTab(tab);

        // Navigate to the appropriate URL
        const newPath = tab === 'stock' ? '/reports/stock-account-statement' : '/reports/money-accounts-statement';
        navigate(newPath, { replace: true });

        // Update the account_id in selectedUserData based on the selected tab
        if (selectedUserData) {
            const newAccountId = tab === 'stock'
                ? selectedUserData.stock_account_id
                : selectedUserData.money_account_id;

            dispatch(setSelectedUserData({
                ...selectedUserData,
                account_id: newAccountId || ''
            }));
        }

        // Trigger API call by updating fetchListTrigger
        setFetchListTrigger(prev => !prev);
    };

    const handleWalletBalanceTabSwitch = (tab: 'etopup-stocks' | 'digital-wallet') => {
        setWalletBalanceActiveTab(tab);
    };

    const handleReversalTabSwitch = (tab: 'recharge-reversal' | 'etopup-stock-digital-wallet-reversal') => {
        setReversalActiveTab(tab);

        // Reset list state immediately to show shimmer instantly
        dispatch(resetState());

        // Navigate to the appropriate URL and clear search params when switching tabs
        const newPath = tab === 'recharge-reversal' ? '/reports/recharge-reversal' : '/reports/etopup-stock-digital-wallet-reversal';
        // Clear search and other filter params when switching tabs, keep only page_number
        const newParams = new URLSearchParams();
        newParams.set('page_number', '1');
        navigate(`${newPath}?${newParams.toString()}`, { replace: true });

        // Trigger API call by updating fetchListTrigger
        setFetchListTrigger(prev => !prev);
    };

    const rotatePlaceholder = useCallback((placeholders: string[]) => {
        if (!Array.isArray(placeholders) || placeholders.length === 0) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setSearchPlaceholder(prev => {
                const trimmedList = placeholders.map(p => p.trim()).filter(Boolean);
                const currentIndex = trimmedList.indexOf(prev);
                const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % trimmedList.length;
                return trimmedList[nextIndex] || prev;
            });
            setIsTransitioning(false);
        }, 500);
    }, []);

    // Define account statement sub-privileges
    const stockAccountPrivilege = 'R12'; // Stock Account Statement privilege
    const moneyAccountPrivilege = 'R15'; // Money Account Statement privilege

    // Check which account statement privileges the user has
    const hasStockAccountPrivilege = hasPrivilegePure(stockAccountPrivilege);
    const hasMoneyAccountPrivilege = hasPrivilegePure(moneyAccountPrivilege);

    const reportsList: ReportTab[] = [
        { name: "eTop Up Recharge Report", route: "/reports/etopup-recharge", privilege: 'R1', searchPlaceholders: 'phone number, transaction id' },
        // { name: "MNO Wise Bundle Balance Activation Report", route: "/reports/mno-bundle-balance-activation", privilege: 'R2', searchPlaceholders: 'phone number, transaction id' },
        // { name: "CVM Commission Disbursement Report", route: "/reports/cvm-commission-disbursement", privilege: 'R3', searchPlaceholders: 'phone number, transaction id' }, // TODO: Not implemented yet
        // { name: "Bundle Activation Report", route: "/reports/bundle-activation", privilege: 'R4', searchPlaceholders: 'phone number, transaction id' }, // TODO: Not implemented yet
        { name: "MNO Wise eTop Up Purchase Report", route: "/reports/mno-etopup-purchase", privilege: 'R5', searchPlaceholders: 'mno name, transaction id, bank/regional office name' },
        { name: "On Target Achievement of MNO Report", route: "/reports/on-target-achievement-mno", privilege: 'R6', searchPlaceholders: 'transaction id, mno name' },
        { name: "Unsuccessful Transaction Report", route: "/reports/unsuccessful-transaction", privilege: 'R7', searchPlaceholders: 'recharged phone number, transaction id' },
        // { name: "MNO Wise Bundle Balance Purchase Report", route: "/reports/mno-bundle-balance-purchase", privilege: 'R8', searchPlaceholders: 'phone number, transaction id' }, // TODO: Not implemented yet
        { name: "Geographical Sales Report", route: "/reports/geographical-sales", privilege: 'R9', searchPlaceholders: 'mno name, country, province, region' },
        { name: "MNO's Service Fee Earned Report", route: "/reports/mno-service-fee-earned", privilege: 'R10', searchPlaceholders: 'transaction id' },
        { name: "Merchant / Customer eTop Up Wallet Balance Report", route: "/reports/merchant-customer-balance", privilege: 'R11', searchPlaceholders: 'user name, user code, phone number' },
        // Include Account Statement Report if user has either privilege
        ...((hasStockAccountPrivilege || hasMoneyAccountPrivilege) ? [
            { name: "Account Statement Report", route: (hasStockAccountPrivilege && activeTab === 'stock') ? "/reports/stock-account-statement" : "/reports/money-accounts-statement", privilege: hasStockAccountPrivilege ? stockAccountPrivilege : moneyAccountPrivilege, searchPlaceholders: 'phone number, transaction id' }
        ] : []),
        { name: "MNO Wise Sales Trend Report", route: "/reports/etop-up-sales-trend", privilege: 'R13', searchPlaceholders: 'mno name' },
        { name: "ZoodPay Profitability Report", route: "/reports/zoodpay-profitability", privilege: 'R14', searchPlaceholders: 'transaction id, MNO name, username, user code, recharged phone number' },
        { name: "Stock Transfer Report", route: "/reports/stock-transfer", privilege: 'R16', searchPlaceholders: 'transaction id, user name, user code' },
        { name: "Reversal Report", route: "/reports/recharge-reversal", privilege: 'R17', searchPlaceholders: 'mno name, transaction id' },
        { name: "CVM Report", route: "/reports/cvm-report", privilege: 'R3', searchPlaceholders: 'transaction id, user code, user name' },
        { name: "Upfront Purchased Report", route: "/reports/upfront-purchased", privilege: 'R18', searchPlaceholders: 'mno name' },
        // { name: "Wallet Transfer Report", route: "/reports/wallet-transfer", privilege: 'R19', searchPlaceholders: 'phone number, transaction id' }, // TODO: Not implemented yet
        // { name: "eTop Up Stock / Digital Wallet Report of Admin / Super Admin", route: "/reports/etopup-digital-wallet-admin", privilege: 'R20', searchPlaceholders: 'phone number, transaction id' }, // TODO: Not implemented yet
        { name: "Upfront Sales Report", route: "/reports/upfront-sales", privilege: 'R21', searchPlaceholders: 'user code and name' },
        { name: "MNO Wise eTop Up Available Balance Report", route: "/reports/mno-etopup-available-balance", privilege: 'R22', searchPlaceholders: 'mno name' }
    ];

    // Filter out those without privilege using pure check (same for all reports including CVM Report / R3)
    const filteredReportsList = reportsList.filter((report) => hasPrivilegePure(report.privilege));
    const firstAccessibleReport = filteredReportsList[0];

    // Special handling for account statement routes - both should be considered as having access if user has the privilege
    const isAccountStatementRoute = location.pathname === '/reports/stock-account-statement' || location.pathname === '/reports/money-accounts-statement';
    const hasAccountStatementAccess = isAccountStatementRoute && (
        (location.pathname === '/reports/stock-account-statement' && hasStockAccountPrivilege) ||
        (location.pathname === '/reports/money-accounts-statement' && hasMoneyAccountPrivilege)
    );

    // Special handling for reversal report routes
    const isReversalReportRoute = location.pathname === '/reports/recharge-reversal' || location.pathname === '/reports/etopup-stock-digital-wallet-reversal';
    const hasReversalReportAccess = isReversalReportRoute && hasPrivilegePure('R17');

    // For reversal report routes, match by checking if it's a reversal route
    const currentReport = isReversalReportRoute
        ? reportsList.find(report => report.name === "Reversal Report")
        : reportsList.find(report => report.route === location.pathname);
    const currentHasAccess = currentReport ? hasPrivilegePure(currentReport.privilege) : false;

    // Redirect to first accessible tab when on /reports or current tab lacks access (but not for valid account statement or reversal routes)
    const isRedirecting = (location.pathname === '/reports' || (!currentHasAccess && !hasAccountStatementAccess && !hasReversalReportAccess)) && !!firstAccessibleReport;
    useEffect(() => {
        if (isRedirecting && firstAccessibleReport) {
            navigate(firstAccessibleReport.route, { replace: true });
        }
    }, [isRedirecting, firstAccessibleReport, navigate]);

    // Handle Account Statement Report sub-tab routing
    useEffect(() => {
        if (type === 'Account Statement Report') {
            // If user only has one privilege, redirect to the appropriate route
            if (hasStockAccountPrivilege && !hasMoneyAccountPrivilege && location.pathname !== '/reports/stock-account-statement') {
                navigate('/reports/stock-account-statement', { replace: true });
            } else if (hasMoneyAccountPrivilege && !hasStockAccountPrivilege && location.pathname !== '/reports/money-accounts-statement') {
                navigate('/reports/money-accounts-statement', { replace: true });
            }
        }
    }, [type, hasStockAccountPrivilege, hasMoneyAccountPrivilege, location.pathname, navigate]);

    // Initialize activeTab based on current route
    useEffect(() => {
        if (type === 'Account Statement Report') {
            if (location.pathname === '/reports/money-accounts-statement') {
                setActiveTab('money');
            } else {
                setActiveTab('stock');
            }
        }
    }, [type, location.pathname]);

    // Initialize reversalActiveTab based on current route
    useEffect(() => {
        if (type === 'Reversal Report') {
            if (location.pathname === '/reports/etopup-stock-digital-wallet-reversal') {
                setReversalActiveTab('etopup-stock-digital-wallet-reversal');
            } else {
                setReversalActiveTab('recharge-reversal');
            }
        }
    }, [type, location.pathname]);

    const renderExtraHeading = ({ entireResponse, loading }: { entireResponse: any; loading: boolean }) => {
        return (
            <>
                <div className="flex items-center pt-4 mr-4 pb-2 pl-4">
                    <Image
                        src={canScrollLeft ? "chevron-left" : "chevron-left-disabled"}
                        className={canScrollLeft ? "cursor-pointer" : "cursor-auto"}
                        onClick={canScrollLeft ? scrollLeft : undefined}
                    />
                    <div
                        ref={scrollRef}
                        className="scrollHide items-center flex-1 overflow-x-auto mx-2"
                        onScroll={checkScrollPosition}
                    >
                        <div className="flex gap-6 min-w-max">
                            {filteredReportsList.map((report, index) => {
                                const isActive = location.pathname === report.route;
                                return (
                                    <div key={index} className={`text-subTitle whitespace-nowrap ${isActive
                                        ? 'text-p_primary border-b-2 border-p_primary'
                                        : 'text-gray-400 border-b-2 border-gray-400'
                                        }`}>
                                        <Link to={report.route}>
                                            {report.name}
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <Image
                        src={canScrollRight ? "chevron-right" : "chevron-right-disabled"}
                        className="cursor-pointer"
                        onClick={canScrollRight ? scrollRight : undefined}
                    />
                </div>
                {renderExtraHeadingsByType({
                    type,
                    entireResponse,
                    loading,
                    userOptions,
                    selectedUser,
                    dropdownLoading,
                    handleUserSelection,
                    loadMoreUsers,
                    handleUserSearch,
                    handleTransactionSearch,
                    selectedUserData,
                    activeTab,
                    hasStockAccountPrivilege,
                    hasMoneyAccountPrivilege,
                    handleTabSwitch,
                    mnoOptions,
                    selectedMnos,
                    handleMnoCheckbox,
                    walletBalanceActiveTab,
                    handleWalletBalanceTabSwitch,
                    // Add filter and export props for Account Statement Report
                    ...(type === "Account Statement Report" && {
                        dynamicFilterConfig: filterConfig,
                        onFilter: (filters: { [key: string]: any } | null) => {
                            const currentParams = new URLSearchParams(location.search);
                            currentParams.set("page_number", "1");

                            if (filters === null && filterConfig) {
                                Object.values(filterConfig).forEach((conf) => {
                                    if (conf.type === "date" && typeof conf.searchKey === "object") {
                                        currentParams.delete(conf.searchKey.start);
                                        currentParams.delete(conf.searchKey.end);
                                    } else if (typeof conf.searchKey === "string") {
                                        currentParams.delete(conf.searchKey);
                                    }
                                });
                            } else if (filters) {
                                Object.entries(filters).forEach(([key, value]) => {
                                    if (
                                        value &&
                                        typeof value === "object" &&
                                        typeof (value as any).format === "function"
                                    ) {
                                        currentParams.set(key, (value as any).format("YYYY-MM-DD"));
                                    } else if (value !== null && value !== undefined && value !== "") {
                                        currentParams.set(key, String(value));
                                    } else {
                                        currentParams.delete(key);
                                    }
                                });
                            }
                            navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
                        },
                        searchParams: new URLSearchParams(location.search),
                        exportEndpoint: (() => {
                            let exportEndpoint = apiEndpoint();
                            if (selectedUserData?.account_id) {
                                exportEndpoint = `${apiEndpoint()}/${selectedUserData.account_id}/statement`;
                            }
                            const exportParams = new URLSearchParams([
                                ...Array.from(new URLSearchParams(location.search).entries()).filter(([key]) => key !== "page_number" && key !== "account_id"  && key !== "cursor"),
                            ]);
                            exportParams.delete('sort');
                            return `${exportEndpoint}/export?${exportParams.toString()}`;
                        })(),
                        disableExport: !selectedUserData || (listState.data && listState.data.length === 0),
                        recordCount: listState.data ? listState.data.length : 0
                    }),
                    // Add filter and export props for Reversal Report
                    ...(type === "Reversal Report" && {
                        dynamicFilterConfig: filterConfig,
                        onFilter: (filters: { [key: string]: any } | null) => {
                            const currentParams = new URLSearchParams(location.search);
                            currentParams.set("page_number", "1");

                            if (filters === null && filterConfig) {
                                Object.values(filterConfig).forEach((conf) => {
                                    if (conf.type === "date" && typeof conf.searchKey === "object") {
                                        currentParams.delete(conf.searchKey.start);
                                        currentParams.delete(conf.searchKey.end);
                                    } else if (typeof conf.searchKey === "string") {
                                        currentParams.delete(conf.searchKey);
                                    }
                                });
                            } else if (filters) {
                                Object.entries(filters).forEach(([key, value]) => {
                                    if (
                                        value &&
                                        typeof value === "object" &&
                                        typeof (value as any).format === "function"
                                    ) {
                                        currentParams.set(key, (value as any).format("YYYY-MM-DD"));
                                    } else if (value !== null && value !== undefined && value !== "") {
                                        currentParams.set(key, String(value));
                                    } else {
                                        currentParams.delete(key);
                                    }
                                });
                            }
                            navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
                        },
                        searchParams: new URLSearchParams(location.search),
                        exportEndpoint: (() => {
                            const exportEndpoint = apiEndpoint();
                            const exportParams = new URLSearchParams([
                                ...Array.from(new URLSearchParams(location.search).entries()).filter(([key]) => key !== "page_number" && key !== "next_cursor"),
                            ]);
                            exportParams.delete('sort');
                            return `${exportEndpoint}/export?${exportParams.toString()}`;
                        })(),
                        disableExport: listState.data && listState.data.length === 0,
                        recordCount: listState.data ? listState.data.length : 0
                    })
                })}
            </>
        );
    };
    // Determine the current report and privilege for the selected tab
    const getCurrentReportName = () => {
        if (isAccountStatementRoute && hasAccountStatementAccess) {
            return "Account Statement Report";
        }
        if (isReversalReportRoute && hasReversalReportAccess) {
            return "Reversal Report";
        }
        return currentHasAccess
            ? (currentReport ? currentReport.name : "eTop Up Recharge Report")
            : (firstAccessibleReport?.name || "eTop Up Recharge Report");
    };

    const currentReportName = getCurrentReportName();
    const getSelectedPrivilege = () => {
        if (type === "Account Statement Report") {
            if (location.pathname === '/reports/money-accounts-statement') {
                return moneyAccountPrivilege;
            } else {
                return stockAccountPrivilege;
            }
        }
        if (isAccountStatementRoute && hasAccountStatementAccess) {
            return location.pathname === '/reports/money-accounts-statement' ? moneyAccountPrivilege : stockAccountPrivilege;
        }
        if (type === "Reversal Report" || (isReversalReportRoute && hasReversalReportAccess)) {
            // Return different privilege based on current pathname (more reliable than state on refresh)
            if (location.pathname === '/reports/etopup-stock-digital-wallet-reversal') {
                return 'R23';
            } else {
                return 'R17';
            }
        }
        return currentHasAccess
            ? (currentReport?.privilege || 'R1')
            : (firstAccessibleReport?.privilege || 'R1');
    };

    const selectedPrivilege = getSelectedPrivilege();
    const hasListPrivilege = hasPrivilegePure(selectedPrivilege) || hasAccountStatementAccess || hasReversalReportAccess;

    const activeReport: ReportTab | undefined = useMemo(() => {
        if (isReversalReportRoute && hasReversalReportAccess) {
            const baseReport = reportsList.find(report => report.name === "Reversal Report");
            if (baseReport) {
                // Return different search placeholders based on current pathname (more reliable than state on refresh)
                if (location.pathname === '/reports/etopup-stock-digital-wallet-reversal') {
                    return {
                        ...baseReport,
                        searchPlaceholders: 'transaction id, user code, user name'
                    } as ReportTab;
                } else {
                    return {
                        ...baseReport,
                        searchPlaceholders: 'mno name, transaction id'
                    } as ReportTab;
                }
            }
            return baseReport as ReportTab | undefined;
        }
        return (currentHasAccess ? currentReport : firstAccessibleReport) as ReportTab | undefined;
    }, [currentHasAccess, currentReport, firstAccessibleReport, isReversalReportRoute, hasReversalReportAccess, reportsList, location.pathname]);
    const activeSearchPlaceholders = useMemo(() => (
        activeReport && activeReport.searchPlaceholders
            ? String(activeReport.searchPlaceholders).split(',').map((s: string) => s.trim()).filter(Boolean)
            : ['phone number', 'transaction id']
    ), [activeReport]);
    const placeholdersKey = activeSearchPlaceholders.join(',');

    // Update search placeholder when activeSearchPlaceholders changes (immediate update for route changes)
    useEffect(() => {
        if (activeSearchPlaceholders && activeSearchPlaceholders.length < 2) {
            setSearchPlaceholder(activeSearchPlaceholders[0]);
        }
    }, [activeSearchPlaceholders, placeholdersKey]);

    useEffect(() => {
        if (activeSearchPlaceholders.length > 1) {
            const interval = setInterval(() => rotatePlaceholder(activeSearchPlaceholders), 2500);
            return () => clearInterval(interval);
        }
    }, [rotatePlaceholder, currentUrl, placeholdersKey, activeSearchPlaceholders]);

    useEffect(() => {
        checkScrollPosition();
    }, []);

    // Scroll to active report on mount and location changes
    useEffect(() => {
        if (scrollRef.current) {
            const activeIndex = filteredReportsList.findIndex(report => report.route === location.pathname);
            if (activeIndex !== -1) {
                const activeElement = scrollRef.current.children[0]?.children[activeIndex] as HTMLElement;
                if (activeElement) {
                    activeElement.scrollIntoView({ behavior: 'smooth', inline: 'center' });
                }
            }
        }
    }, [location.pathname]);

    // Handle tab/type changes
    useEffect(() => {
        dispatch(setUserUrl(null));
        dispatch(resetPhoneNumbers());
        if ((location.pathname !== '/reports/stock-account-statement') && (location.pathname !== '/reports/money-accounts-statement')) {
            dispatch(clearSelectedUser());
        }
        // Update search placeholder immediately when route changes
        if (activeSearchPlaceholders && activeSearchPlaceholders.length > 0) {
            setSearchPlaceholder(activeSearchPlaceholders[0]);
        }

        // Clear sort parameters when switching between different report types
        const currentParams = new URLSearchParams(location.search);
        if (currentParams.has('sort')) {
            currentParams.delete('sort');
            const newSearch = currentParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            navigate(newUrl, { replace: true });
        }

        // Handle Reversal Report routing
        if (type === 'Reversal Report') {
            // If on reversal report but not on a valid sub-route, redirect to recharge-reversal
            if (!isReversalReportRoute) {
                navigate('/reports/recharge-reversal', { replace: true });
            }
        }
    }, [dispatch, type, placeholdersKey, location.pathname, navigate, isReversalReportRoute]);

    // Handle URL changes for previous URL tracking
    useEffect(() => {
        dispatch(setPreviousUrl(currentUrl));
    }, [dispatch, currentUrl]);

    // Track previous type to detect type changes
    const prevTypeRef = useRef<string>(type);
    // Track if initial mount has completed to prevent double API calls
    const isInitialMountRef = useRef<boolean>(true);

    // Handle type changes - reset state and clear sort params
    useEffect(() => {
        const isTypeChanged = prevTypeRef.current !== type;

        if (isTypeChanged) {
            // Reset list state when type changes to prevent stale data from previous tab
            dispatch(resetState());
            
            // Clear sort parameters when report type changes to prevent cross-contamination
            const currentParams = new URLSearchParams(location.search);
            if (currentParams.has('sort')) {
                currentParams.delete('sort');
                const newSearch = currentParams.toString();
                const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
                navigate(newUrl, { replace: true });
            }
        }

        // Update the ref for next comparison
        prevTypeRef.current = type;

        // Only trigger fetch for user selection changes (not on initial mount or type changes)
        // Type changes are handled by GridTable's searchParams watching
        // Initial mount is handled by GridTable's initial useEffect
        if (!isInitialMountRef.current && !isTypeChanged && (selectedUser || selectedUserData)) {
            setFetchListTrigger((prev) => !prev);
        }
        
        // Mark initial mount as complete after first render
        isInitialMountRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, selectedUser, selectedUserData, dispatch, navigate]);

    // Get the list state to access the API response
    const listState = useSelector((state: RootState) => state.list);
    const { entireResponse } = listState;

    // Update MNO options when entireResponse changes
    useEffect(() => {
        if (type === 'MNO Wise eTop Up Purchase Report' && entireResponse?.mno_summary) {
            const mnos = entireResponse?.mno_summary.map((mno: any) => ({
                id: mno.mno_id,
                name: mno.mno_name
            }));
            setMnoOptions(mnos);
        }
    }, [entireResponse, type]);

    // Reset selectedMnos when report type changes
    useEffect(() => {
        setSelectedMnos(['All']);
    }, [type]);

    // Reset MNO options when report type changes
    useEffect(() => {
        if (type !== 'Geographical Sales Report' && type !== 'Upfront Purchase Report') {
            setMnoOptions(undefined);
            setMnoApiFailed(false);
            mnoFetchedRef.current = false;
        }
    }, [type]);

    // Initialize selectedMnos from URL parameters
    useEffect(() => {
        if (type === 'MNO Wise eTop Up Purchase Report') {
            const mnoIdsParam = searchParams.get('mno_ids');
            if (mnoIdsParam) {
                const mnoIds = mnoIdsParam.split(',');
                setSelectedMnos(mnoIds);
            }
        } else if (type === "MNO's Service Fee Earned Report") {
            const mnoIdsParam = searchParams.get('mno_id');
            if (mnoIdsParam) {
                const mnoIds = mnoIdsParam.split(',');
                setSelectedMnos(mnoIds);
            }
        } else if (type === 'Upfront Purchase Report' || type === 'Geographical Sales Report') {
            const mnoIdsParam = searchParams.get('mno_ids');
            if (mnoIdsParam) {
                const mnoIds = mnoIdsParam.split(',');
                setSelectedMnos(mnoIds);
            } else {
                // Reset to 'All' when mno_ids is cleared from URL
                setSelectedMnos(['All']);
            }
        }
    }, [type, searchParams]);

    // Fetch MNO names for Geographical Sales Report and Upfront Purchase Report
    useEffect(() => {
        if ((type === 'Geographical Sales Report' || type === 'Upfront Purchase Report') && !mnoFetchedRef.current && !mnoApiFailed) {
            mnoFetchedRef.current = true;
            const loadMnoNames = async () => {
                try {
                    const mnos = await fetchMnoNames();
                    if (mnos && mnos.length > 0) {
                        setMnoOptions(mnos);
                        setMnoApiFailed(false);
                    } else {
                        setMnoOptions(undefined);
                        setMnoApiFailed(true);
                    }
                } catch (error) {
                    console.error('Failed to fetch MNO names:', error);
                    mnoFetchedRef.current = false;
                    setMnoOptions(undefined);
                    setMnoApiFailed(true);
                }
            };
            loadMnoNames();
        }
    }, [type, mnoApiFailed]);

    const handleMnoCheckbox = (mnoName: string, checked: boolean) => {
        let newSelectedMnos: string[] = [];

        if (mnoName === 'All') {
            newSelectedMnos = checked ? ['All'] : [];
        } else if (mnoOptions) {
            const selectedMno = mnoOptions.find(mno => mno.name === mnoName);
            const mnoId = selectedMno?.id;
            if (mnoId) {
                if (checked) {
                    // Adding an MNO
                    if (selectedMnos.includes('All')) {
                        // If All was selected, keep all MNOs selected
                        newSelectedMnos = mnoOptions.map(mno => mno.id);
                    } else {
                        // Add to current selection
                        newSelectedMnos = [...selectedMnos, mnoId];
                        // Check if all MNOs are now selected
                        if (newSelectedMnos.length === mnoOptions.length) {
                            newSelectedMnos = ['All'];
                        }
                    }
                } else {
                    // Removing an MNO
                    if (selectedMnos.includes('All')) {
                        // If All was selected, select all except this one
                        newSelectedMnos = mnoOptions.map(mno => mno.id).filter(id => id !== mnoId);
                    } else {
                        // Remove from current selection
                        newSelectedMnos = selectedMnos.filter(id => id !== mnoId);
                    }
                }
            }
        }

        setSelectedMnos(newSelectedMnos);

        // Update URL parameters with mno_ids
        const currentParams = new URLSearchParams(location.search);
        if (newSelectedMnos.includes('All') || newSelectedMnos.length === 0) {
            currentParams.delete('mno_ids');
        } else {
            currentParams.set('mno_ids', newSelectedMnos.join(','));
        }
        currentParams.set('page_number', '1'); // Reset to first page
        navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });

        setFetchListTrigger(prev => !prev);
    };
    const staticText = {
        emptyScreenText: {
            heading1: type === "Account Statement Report" ?
                ((selectedUser !== '') ?
                    "No transactions found for this user"
                    : "Use the dropdown to find and display details") :
                `There are no transactions yet`,
            heading2: type === "Account Statement Report" ?
                (selectedUser !== '' ?
                    "No transactions found for this user"
                    : "Search by user name or user code to get started") :
                ``,
        },
    };
    // For Reversal Report, determine table details based on pathname instead of activeTab
    const tableDetails = type === "Reversal Report" 
        ? getTableDetailsByType(type, location.pathname === '/reports/etopup-stock-digital-wallet-reversal' ? 'etopup-stock-digital-wallet-reversal' : 'recharge-reversal')
        : getTableDetailsByType(type, activeTab);
    const filterConfig = getFilterConfigByType(type, mnoOptions, mnoApiFailed);
    const apiEndpoint = () => {
        if (type === "Unsuccessful Transaction Report") {
            return unsuccessfulTransactionReports;
        } else if (type === "eTop Up Recharge Report") {
            return etopupRechargeReports;
        } else if (type === "Stock Transfer Report") {
            return etopupPurchaseOrderReports;
        } else if (type === "Account Statement Report") {
            // Determine which account statement endpoint to use based on active tab
            if (activeTab === 'money') {
                return moneyAccountStatement;
            } else {
                // Default to stock account statement
                return stockAccountStatement;
            }
        } else if (type === "MNO Wise eTop Up Purchase Report") {
            return mnoWiseEtopupPurchaseReports;
        } else if (type === "MNO Wise eTop Up Available Balance Report") {
            return mnoWiseEtopupAvailableBalanceReports;
        } else if (type === "Upfront Sales Report") {
            return upfrontSales;
        } else if (type === "Upfront Purchase Report") {
            return upfrontPurchaseReports;
        } else if (type === "MNO's Service Fee Earned Report") {
            return mnoServiceFeeEarnedReports;
        } else if (type === "Merchant / Customer eTop Up Wallet Balance Report") {
            return customerMerchantBalance;
        } else if (type === "Geographical Sales Report") {
            return geographicalSalesReports;
        } else if (type === "On Target Achievement of MNO Report") {
            return onTargetAchievementOfMNO;
        } else if (type === "Reversal Report") {
            // Determine which reversal endpoint to use based on current pathname (more reliable than state on refresh)
            if (location.pathname === '/reports/etopup-stock-digital-wallet-reversal') {
                return etopupStockReversal;
            } else {
                return reversalReports; // Recharge Reversal endpoint
            }
        } else if (type === "ZoodPay Profitability Report") {
            return profitabilityReport;
        } else if (type === "CVM Report") {
            return cvmReport;
        } else if (type === "MNO Wise Sales Trend Report") {
            return etopUpSalesTrendReports;
        } else {
            return etopupRechargeReports;
        }
    };
    // Create dynamic static row data based on API response and filter state
    const createStaticRowData = () => {
        if (type !== "Account Statement Report") return undefined;

        // Check if date filter is applied
        const hasDateFilter = searchParams.get('transaction_start_date') && searchParams.get('transaction_end_date');

        // Get data from API response if available
        const closingBalance = entireResponse?.closing_balance || "0.00";

        // Format dates
        const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).replace(/\//g, '-');
        };

        const currentDate = new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        // Top static row (closing balance)
        const topRowData = {
            show: true,
            position: 'top' as const,
            data: {
                noticeMessage: hasDateFilter
                    ? `As on ${formatDate(searchParams.get('transaction_end_date')!)} Closing Balance is AFA ${closingBalance}`
                    : `As on ${currentDate} Closing Balance is AFA ${closingBalance}`,
                lastColumnValue: `AFA ${closingBalance}`
            }
        };

        return topRowData;
    };

    // Create bottom static row data (opening balance)
    const createBottomStaticRowData = () => {
        if (type !== "Account Statement Report") return undefined;

        // Check if date filter is applied
        const hasDateFilter = searchParams.get('transaction_start_date') && searchParams.get('transaction_end_date');

        // Get data from API response if available
        const openingBalance = entireResponse?.opening_balance || "0.00";

        // Format dates
        const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).replace(/\//g, '-');
        };

        return {
            show: true,
            position: 'bottom' as const,
            data: {
                noticeMessage: hasDateFilter
                    ? `As on ${formatDate(searchParams.get('transaction_start_date')!)} Opening Balance is AFA ${openingBalance}`
                    : `Opening Balance AFA ${openingBalance}`,
                lastColumnValue: `AFA ${openingBalance}`
            }
        };
    };

    const staticRowData = createStaticRowData();
    const bottomStaticRowData = createBottomStaticRowData();

    // Create static summary row for Upfront Sales Report
    const createUpfrontSalesStaticRow = () => {
        if (type !== "Upfront Sales Report") return undefined;
        // Use data from entireResponse if available, otherwise use default values
        const totalStockSalesValue = entireResponse?.total_amount || "0.00";
        const totalCommissionPaid = entireResponse?.avg_commission_percentage || "0.00";
        const totalCommissionPayout = entireResponse?.total_commission_amount || "0.00";

        return {
            show: true,
            position: 'bottom' as const,
            data: {
                firstColumnSpan: 2, // Span first two columns
                firstColumnText: "COGS%", // Text for the merged first column
                remainingColumns: [
                    `AFA ${Number(totalStockSalesValue).toFixed(2)}`, // Stock Sales Value
                    `${Number(totalCommissionPaid).toFixed(2)}%`,     // Upfront Commission Paid (%)
                    `AFA ${Number(totalCommissionPayout).toFixed(2)}` // Upfront Commission Payout Value
                ],
                textColor: "text-p_primary",
                fontWeight: "font-semibold"
            }
        };
    };

    const upfrontSalesStaticRow = useMemo(() => createUpfrontSalesStaticRow(), [entireResponse, type]);

    // Create static summary row for Upfront Purchase Report
    const createUpfrontPurchaseStaticRow = () => {
        if (type !== "Upfront Purchase Report") return undefined;
        // Use data from entireResponse if available, otherwise use default values
        const totalStockPurchaseValue = entireResponse?.total_stock_purchase_value || 0;
        const totalUpfrontCommissionReceivedValue = entireResponse?.total_upfront_commission_received_value || 0;
        // Use gross_profit_margin directly from API response
        const grossProfitMargin = entireResponse?.gross_profit_margin !== undefined
            ? Number(entireResponse.gross_profit_margin).toFixed(2)
            : "0.00";

        return {
            show: true,
            position: 'bottom' as const,
            data: {
                firstColumnSpan: 2, // Span first two columns (Date and MNO Name)
                firstColumnText: "Gross Profit Margin (%)", // Text for the merged first column
                remainingColumns: [
                    `AFA ${Number(totalStockPurchaseValue).toFixed(2)}`, // Total Stock Purchase Value (column 3)
                    `${grossProfitMargin}%`, // Gross Profit Margin (column 4 - shown in Upfront Commission Received % column)
                    `AFA ${Number(totalUpfrontCommissionReceivedValue).toFixed(2)}` // Total Upfront Commission Received Value (column 5)
                ],
                textColor: "text-p_primary",
                fontWeight: "font-semibold"
            }
        };
    };

    const upfrontPurchaseStaticRow = useMemo(() => createUpfrontPurchaseStaticRow(), [entireResponse, type]);

    // Render summary for Account Statement Report
    const renderSummary = ({ entireResponse, loading }: {
        entireResponse: {
            total_dr?: string;
            total_cr?: string;
            closing_balance?: string;
        } | null,
        loading: boolean
    }) => {
        if (type !== "Account Statement Report" || loading || !entireResponse) return null;

        const totalDebit = entireResponse?.total_dr || "0.00";
        const totalCredit = entireResponse?.total_cr || "0.00";
        const totalBalance = entireResponse?.closing_balance || "0.00";

        return (
            <div className="flex justify-end items-center pt-4 border-t ">
                <div className="flex text-sm font-medium">
                    <span className="border-r-2 border-secondary_text px-4">
                        Total  <span className="text-p_primary font-semibold">AFA {totalDebit}</span>
                    </span>
                    <span className="border-r-2 border-secondary_text px-4">
                        Total Credit <span className="text-p_primary font-semibold">AFA {totalCredit}</span>
                    </span>
                    <span className=" pl-4">
                        Total Balance <span className="text-p_primary font-semibold">AFA {totalBalance}</span>
                    </span>
                </div>
            </div>
        );
    };

    // Move useMemo outside of conditional return to fix React Hook rules
    const tableParams = useMemo(() => {
        // Start with base params
        const currentParams: { [key: string]: string } = { ...params };

        // Add ALL URL parameters to the API call - no filtering or conditions
        const urlParams = new URLSearchParams(location.search);
        urlParams.forEach((value, key) => {
            currentParams[key] = value;
        });

        // Special handling for MNO selection in MNO Wise eTop Up Purchase Report
        if (type === "MNO Wise eTop Up Purchase Report") {
            const mnoIdsFromUrl = searchParams.get('mno_ids');
            if (mnoIdsFromUrl) {
                currentParams.mno_ids = mnoIdsFromUrl;
            } else if (!selectedMnos.includes('All') && selectedMnos.length > 0) {
                currentParams.mno_ids = selectedMnos.join(',');
            }
        } else if (type === "On Target Achievement of MNO Report") {
            const sort = searchParams.get('sort');
            if (sort) {
                delete currentParams.sort;
            }
        }

        // Special handling for MNO selection in Upfront Purchase Report and Geographical Sales Report
        if (type === "Upfront Purchase Report" || type === "Geographical Sales Report") {
            const mnoIdsFromUrl = searchParams.get('mno_ids');
            if (mnoIdsFromUrl) {
                // Only use mno_ids from URL if it exists
                currentParams.mno_ids = mnoIdsFromUrl;
            } else {
                // Explicitly remove mno_ids if it's not in URL (filter was cleared)
                delete currentParams.mno_ids;
            }
            // Don't add mno_ids from selectedMnos state - only use URL params
            // This ensures that when filter is cleared, mno_ids is properly removed
        }

        return currentParams;
    }, [type, selectedMnos, searchParams, params, location.search]);

    const renderTopBarExtras = () => {
        return renderTopBarExtrasByType({
            type,
            mnoOptions,
            selectedMnos,
            handleMnoCheckbox,
            entireResponse,
            reversalActiveTab,
            handleReversalTabSwitch
        });
    };

    const handleActions = (key: any, value: any, balanceType: any) => {
        // Set the mno_id in Redux before navigating so that ListOfBankTransactions can use it for external-wallet API
        if (value?.mno_id) {
            dispatch(setSelectedBankId(value.mno_id));
        }
        
        if (balanceType === "bundle_balance") {
            navigate(`/reports/mno-etopup-available-balance/bundle-activation-mno-inventory-gl/${value?.bundle_balance_account_id}?page_number=1&group_type=Bundle+Activation+MNO+Inventory+GL&group=Assets`)
        } else if (balanceType === "etopup_balance") {
            navigate(`/reports/mno-etopup-available-balance/etopup-mno-inventory-gl/${value?.etopup_balance_account_id}?page_number=1&group_type=Etopup+MNO+Inventory+GL&group=Assets`)
        }
    }

    return isRedirecting ? (
        <div>
            <div className="flex gap-4 bg-white h-[86px] mb-3 rounded-md items-center p-4"></div>
            <div className="p-4 mt-1 rounded-md bg-white h-maintableheight"></div>
        </div>
    ) : (
        <>
            <Breadcrumbs
                navPath={["Reports", `${currentReportName}`]}
                heading={`${currentReportName}`}
                list={true}
            />
            <GridTable
                staticText={staticText}
                noUserSelectedText={
                    type === "Account Statement Report"
                        ? {
                            heading1: "Use the dropdown to find and display details",
                            heading2: "Search by user name or user code to get started",
                        }
                        : undefined
                }
                mainEndPoint={apiEndpoint()}
                handleActions={handleActions}
                TableDetails={tableDetails}
                params={tableParams}
                extraParams={undefined}
                searchPlaceholder={`Search by ${searchPlaceholder}`}
                placeholderClassName={`search-placeholder ${isTransitioning ? "transitioning" : ""
                    }`}
                filters={type === "MNO Wise eTop Up Available Balance Report" || type === "Merchant / Customer eTop Up Wallet Balance Report" ? false : true}
                exporting={type === "On Target Achievement of MNO Report" ? false : true}
                noSearch={type === "Account Statement Report" ? true : false}
                renderExtraHeading={renderExtraHeading}
                renderSummary={
                    type === "Account Statement Report" ? renderSummary : undefined
                }
                notfoundRoute={{ state: { route: `/reports/etopup-recharge` } }}
                title={`List of eTop Up Recharge`}
                dynamicFilterConfig={filterConfig || undefined}
                hasActivateDeactivateMDPrivilege={true}
                hasViewMDPrivilege={viewPrivilege}
                heightClassName={
                    (type === "Account Statement Report" || type === "MNO's Service Fee Earned Report")
                        ? " "
                        : (type === "Merchant / Customer eTop Up Wallet Balance Report" || type === "Reversal Report")
                            ? "h-commentSectioneHeight"
                            : "h-transactionListHeight"
                }
                hasEditMDPrivilege={true}
                addMorePadding={false}
                fetchListTrigger={fetchListTrigger}
                hasListPrivilege={hasListPrivilege}
                staticRowData={staticRowData}
                customHeight={
                    type === "Account Statement Report"
                        ? "h-[101px] md:h-[103px]"
                        : "h-[101px] lg:h-[115px]"
                }
                bottomStaticRowData={
                    type === "Account Statement Report"
                        ? bottomStaticRowData
                        : type === "Upfront Sales Report"
                            ? upfrontSalesStaticRow
                            : type === "Upfront Purchase Report"
                                ? upfrontPurchaseStaticRow
                                : undefined
                }
                selectedUser={
                    type === "Account Statement Report"
                        ? (selectedUserData &&
                            ((activeTab === 'stock' && selectedUserData.stock_account_id) ||
                                (activeTab === 'money' && selectedUserData.money_account_id))
                            ? selectedUserData
                            : null)
                        : undefined
                }
                requireUserSelection={type === "Account Statement Report"}
                useAccountIdAsPath={type === "Account Statement Report"}
                excludeSearchParam={type === "Account Statement Report"}
                reportType={type}
                activeTab={activeTab}
                mainDivScroll={true}
                renderTopBarExtras={renderTopBarExtras}
                paginateMarginTop={0}
                customEmptyScreenHeight={type === "Account Statement Report" ? "h-[8rem] mt-8" : undefined}
                forceFullWidth={true}
            />
        </>
    );
}